import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { BrimiLogo } from "@/components/OwlMascot";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PWAInstallButton } from "@/components/PWAInstallButton";
import { LogOut, LayoutDashboard, GraduationCap, Crown, Shield } from "lucide-react";

const Navbar = () => {
  const { user, logout, billing } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const isOwner = user?.role === "owner";

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
      {/* Trial / billing banner */}
      {user && billing && billing.kind === "trial" && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-mono py-1.5 px-6 flex items-center justify-center gap-3" data-testid="trial-banner">
          <span>⏳ Free trial — {billing.days_left} day{billing.days_left !== 1 ? "s" : ""} left</span>
          <Link to="/paywall" className="font-bold underline">Upgrade</Link>
        </div>
      )}
      {user && billing && billing.kind === "expired" && (
        <div className="bg-rose-100 border-b border-rose-200 text-rose-800 text-xs font-mono py-1.5 px-6 flex items-center justify-center gap-3" data-testid="expired-banner">
          <span>Your trial has ended.</span>
          <Link to="/paywall" className="font-bold underline">Subscribe to continue</Link>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="nav-home-link">
          <BrimiLogo size={36} />
          <div className="leading-tight min-w-0">
            <div className="font-outfit font-black text-base sm:text-lg text-slate-900 truncate">BrimiAI</div>
            <div className="hidden sm:block text-[10px] uppercase tracking-widest text-blue-600 font-semibold">Virtual Science Lab</div>
          </div>
        </Link>
        <nav className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <PWAInstallButton />
          {user ? (
            <>
              <Link to="/labs" data-testid="nav-labs-link">
                <Button variant="ghost" className="font-medium">Labs</Button>
              </Link>
              <Link to={isOwner ? "/admin" : user.role === "teacher" ? "/teacher" : "/dashboard"} data-testid="nav-dashboard-link">
                <Button variant="ghost" className="font-medium">
                  {isOwner ? <Shield className="mr-2 h-4 w-4" /> : user.role === "teacher" ? <GraduationCap className="mr-2 h-4 w-4" /> : <LayoutDashboard className="mr-2 h-4 w-4" />}
                  {isOwner ? "Admin" : "Dashboard"}
                </Button>
              </Link>
              <div className="hidden md:flex items-center gap-3 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 ml-2">
                <div className={`w-8 h-8 rounded-full text-white flex items-center justify-center font-bold text-sm ${isOwner ? "bg-gradient-to-br from-amber-400 to-orange-600" : "bg-blue-600"}`}>
                  {user.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="text-sm">
                  <div className="font-semibold text-slate-900 leading-tight flex items-center gap-1.5" data-testid="nav-user-name">
                    {user.name}
                    {isOwner && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[9px] font-black uppercase tracking-wider" data-testid="owner-badge">
                        <Crown className="h-2.5 w-2.5" /> Owner
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-blue-600 font-mono">⚡ {user.xp} XP</div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onLogout} data-testid="nav-logout-btn" aria-label="Log out">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link"><Button variant="ghost" size="sm" className="px-2 sm:px-4">Log in</Button></Link>
              <Link to="/signup" data-testid="nav-signup-link">
                <Button size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5 transition-transform px-3 sm:px-5 text-xs sm:text-sm">
                  Get started
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
